/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type { AmiMeterSubType } from '../../api/ami';

export interface MeterType {
  id: string;
  name: string;
  registers: ReadonlySet<string> | null;
  threePhase: boolean;
}

export type MeterTypeIndex = ReadonlyMap<string, MeterType>;

const GRN = /^(PQ_)?\d+\.\d+\.\d+_\d+$/;

function objectOrParsed(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return objectOrParsed(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

export function subTypeRegisterCodes(subType: AmiMeterSubType | null | undefined): string[] | null {
  const definition = objectOrParsed(subType?.typeDefinition);
  const mapping = objectOrParsed(definition?.registerMapping) ?? objectOrParsed(subType?.registerMapping);
  if (!mapping) {
    return null;
  }
  const codes = Object.keys(mapping).filter((key) => GRN.test(key));
  return codes.length > 0 ? codes : null;
}

export function toMeterType(subType: AmiMeterSubType | null | undefined): MeterType | null {
  const id = subType?.subTypeId;
  if (id === undefined || id === null || id === '') {
    return null;
  }
  const codes = subTypeRegisterCodes(subType);
  const definition = objectOrParsed(subType?.typeDefinition);
  const threePhase = (definition?.isThreePhase ?? subType?.isThreePhase) === true;
  return { id: String(id), name: subType?.subTypeName ?? String(id), registers: codes ? new Set(codes) : null, threePhase };
}

export function buildMeterTypeIndex(subTypes: AmiMeterSubType[] | null | undefined): MeterTypeIndex {
  const index = new Map<string, MeterType>();
  (Array.isArray(subTypes) ? subTypes : []).forEach((subType) => {
    const type = toMeterType(subType);
    if (type) index.set(type.id, type);
  });
  return index;
}

export function commonRegisters(types: (MeterType | null | undefined)[]): ReadonlySet<string> | null {
  const known = types.map((type) => type?.registers).filter((codes): codes is ReadonlySet<string> => !!codes);
  if (known.length === 0) {
    return null;
  }
  return known.slice(1).reduce<ReadonlySet<string>>(
    (common, next) => new Set([...common].filter((code) => next.has(code))),
    known[0],
  );
}
